import { solRefusalGuidance } from "./sol-refusal";
import React from "react";
import { Button } from "@/ds";
import { Icon, fmtStamp } from "@/lib";
import type { RequestDetail } from "@/api";
import { workspaceView } from "../../api/workspace-view";
import { useT } from "../../i18n";
import styles from "./sol-guidance.module.css";
import { WorkspaceTasks } from "./workspace-tasks";

export type GuidanceTarget = "facts" | "conflicts" | "sources" | "tasks" | "processing" | "draft" | "questions" | "delivery" | "none";
export interface SolGuidanceItem { id: string; taskId?: string; title: string; values?: { n: number }; body?: string; bodyValues?: { time: string }; detail?: string; target: GuidanceTarget; tone: "warning" | "danger" | "info" }
const SOURCE_TASKS = new Set(["missing_price", "missing_service_source", "source_conflict", "suitability_unknown"]);
// These describe the retained reply, not the current result's operator work without a reply.
const DRAFT_BLOCKERS = new Set(["already_sent", "request_not_sendable", "draft_missing", "draft_stale",
  "draft_rejected", "artifact_unavailable", "attachments_unavailable", "approver_name_required", "closing_regeneration_required"]);

export function solHasCurrentResultWithoutDraft(data: RequestDetail): boolean {
  const view = workspaceView(data);
  return view.engine === "single_agent" && view.current && view.activeWorkflow?.payload?.draft === null
    && !view.processing?.in_progress && !data.request.trashed_at && !["sent", "closed"].includes(data.request.status);
}

/** Display selection only. Every command continues to use the server's ordinary guards. */
export function solGuidanceItems(data: RequestDetail, assessmentPending = false, sendRecoveryState?: "awaiting_authority" | "uncertain" | null): SolGuidanceItem[] {
  const view = workspaceView(data), flow = view.activeWorkflow, processing = view.processing;
  if (!sendRecoveryState && ["SENT", "CLOSED_REQUEST"].includes(data.review_guidance.summary_code)) return [{
    id: "terminal", title: `ws.guidance.summary.${data.review_guidance.summary_code}`, target: "none", tone: "info",
  }];
  const items: SolGuidanceItem[] = [];
  // A lost send response is local evidence of an unresolved side effect. Cached server
  // readiness must not replace its warning before an authoritative refresh settles it.
  if (sendRecoveryState) items.push({ id: "delivery-recovery",
    title: sendRecoveryState === "uncertain" ? "ws.guidance.summary.DELIVERY_UNCERTAIN" : "ws.review.awaitingAuthoritativeState",
    body: "ws.review.retryBlocked", target: "delivery", tone: sendRecoveryState === "uncertain" ? "danger" : "info" });
  if (!sendRecoveryState && ["DELIVERY_UNCERTAIN", "SENDING_IN_PROGRESS", "SUBMISSION_PENDING", "SUBMISSION_FAILED"].includes(data.review_guidance.summary_code)) {
    items.push({ id: "delivery", title: `ws.guidance.summary.${data.review_guidance.summary_code}`, target: "delivery",
      tone: ["DELIVERY_UNCERTAIN", "SUBMISSION_FAILED"].includes(data.review_guidance.summary_code) ? "danger" : "info" });
  }
  if (processing && ["failed", "uncertain", "deferred"].includes(processing.phase)) {
    items.push({ id: "operation", ...solRefusalGuidance(data.workflow!),
      bodyValues: processing.retry_at ? { time: fmtStamp(processing.retry_at) } : undefined,
      target: !assessmentPending && flow?.conflict?.fields.length ? "conflicts" : "processing" });
  } else if (processing?.in_progress) {
    items.push({ id: "operation", ...(data.workflow?.reason === "persistence_unavailable"
      ? solRefusalGuidance(data.workflow) : { title: `workflow.phase.${processing.phase}`, body: "workflow.next.wait" }),
      target: "processing", tone: "info" });
  }
  if (data.workflow?.recalculation_required) items.push({ id: "recalculate", title: "workflow.recalculationRequired",
    body: data.workflow.recalculation_waiting ? "solGuide.permission.previous_attempt_finishing" : "workflow.recalculationRequiredBody", target: "processing", tone: "warning" });
  const readiness = data.send_readiness;
  const tasksWithoutDraft = solHasCurrentResultWithoutDraft(data);
  const processingExplained = items.some(item => item.id === "operation" || item.id === "recalculate");
  for (const blocker of readiness?.blockers ?? [{ code: "readiness_unavailable", actions: [] }]) {
    if (blocker.code === "task_open") continue; // The task's typed guidance below owns its action.
    if (tasksWithoutDraft && DRAFT_BLOCKERS.has(blocker.code)) continue;
    if (processingExplained && ["draft_missing", "draft_stale", "workflow_unavailable"].includes(blocker.code)) continue;
    if ((sendRecoveryState || items.some(item => item.id === "delivery")) && ["delivery_unknown", "delivery_pending"].includes(blocker.code)) continue;
    items.push({ id: `send:${blocker.code}`, title: `sendReady.blocker.${blocker.code}`,
      target: ["delivery_unknown", "delivery_pending"].includes(blocker.code) ? "delivery"
        : blocker.actions.includes("open_processing") ? "processing" : "tasks",
      tone: blocker.code === "delivery_unknown" ? "danger" : "warning" });
  }
  const blockingTasks = !tasksWithoutDraft && readiness && new Set(readiness.blockers.flatMap(item => item.task_id ? [item.task_id] : []));
  const seen = new Set<string>();
  for (const task of view.tasks) {
    const source = SOURCE_TASKS.has(task.reason_code);
    // Without a reply, unresolved source information still explains what to repair before
    // another generation. It need not be an approval blocker to own that next step.
    const sourceWithoutDraft = tasksWithoutDraft && source && !task.resolution && !task.decision;
    // The server checklist owns which points block this reply. Information and points settled
    // at send stay in that checklist; neither is the operator's next required action.
    if (!sourceWithoutDraft && (!task.open || (blockingTasks ? !blockingTasks.has(task.id) : task.reply_confirmable))) continue;
    const id = `task:${task.reason_code}:${task.basis_hash}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const actionable = task.open && Boolean(task.operator_action);
    items.push({ id, taskId: task.id, title: source && !actionable ? "solGuide.sources" : `workflow.task.${task.disagreement ? "fact_disagreement" : task.reason_code}`,
      body: task.disagreement ? "sendReady.disagreement.hint" : source && task.operator_action !== "done" ? "solGuide.sourcesBody" : undefined,
      target: actionable ? "tasks" : source ? "sources" : "tasks", tone: "warning" });
    // "Done" settles a point of an offer in one click; a source update is not the step there.
    if (source && actionable && task.operator_action !== "done") items.push({ id: `${id}:source`, taskId: task.id, title: "solGuide.sources", body: "solGuide.sourcesBody", target: "sources", tone: "warning" });
  }
  // Preserve independent delivery/approval conditions, while replacing derivative stale-draft
  // and no-draft notices with the current result's cause above.
  const represented = new Set(["STALE_DRAFT", "MISSING_DRAFT", "DRAFT_REJECTED", "REQUEST_NOT_SENDABLE", "HUMAN_REVIEW_REQUIRED", "DRAFT_APPROVAL_REQUIRED", "MISSING_DECISION", "DELIVERY_UNCERTAIN", "SENDING_IN_PROGRESS", "SUBMISSION_PENDING"]);
  // Core uses this code for any non-current Sol result. The phase above owns that
  // explanation; missing safety data or a mismatched current result remains independent.
  if (data.workflow && !data.workflow.current) represented.add("DECISION_SAFETY_BLOCKED");
  if (readiness?.blockers.some(item => item.code === "sending_disabled")) represented.add("SENDING_DISABLED");
  if (readiness?.blockers.some(item => item.code === "attachments_unavailable")) represented.add("ATTACHMENT_STORAGE_UNAVAILABLE");
  for (const blocker of [data.review_guidance.primary_blocker, ...data.review_guidance.additional_blockers]) {
    if (!blocker || represented.has(blocker.code)) continue;
    represented.add(blocker.code);
    items.push({ id: blocker.code, title: solSummaryKey(blocker.code), target: "processing", tone: blocker.severity === "danger" ? "danger" : "warning" });
  }
  const currentDraft = view.current && !!flow?.payload?.draft && data.draft?.is_current_decision === true;
  if (!items.length) {
    const title = data.draft?.human_rejected && currentDraft ? "solGuide.rejected" : currentDraft ? "solGuide.review" : view.current ? "solGuide.noReply" : "solGuide.noCurrent";
    items.push({ id: "result", title, target: currentDraft ? "draft" : "processing", tone: currentDraft ? "info" : "warning",
      body: currentDraft ? (flow?.commercial?.proposal ? "solGuide.offerBody" : "solGuide.replyBody") : undefined });
  }
  if (!assessmentPending && view.requirements?.length) {
    items.push({ id: "requirements", title: "solFacts.customerAnswers",
      body: "solGuide.factsBody", target: "facts", tone: "info" });
  }
  const priority = (item: SolGuidanceItem) => item.id === "delivery-recovery" ? 2 : Number(item.tone === "danger");
  return items.sort((a, b) => priority(b) - priority(a));
}

export function solSummaryKey(code: string): string {
  return ["DECISION_SAFETY_BLOCKED", "DECISION_SAFETY_UNAVAILABLE", "STALE_DRAFT", "MISSING_DECISION"].includes(code)
    ? `solGuide.summary.${code}` : `ws.guidance.summary.${code}`;
}

/** One persistent status owner for every review tab; details contain supporting evidence. */
export function SolNextStep({ items, conflictFields, onDetails, onNavigate, localChanges, compact = false }: {
  compact?: boolean;
  items: SolGuidanceItem[]; conflictFields?: readonly string[]; onDetails: () => void; onNavigate: (target: GuidanceTarget) => void;
  localChanges: boolean;
}) {
  const t = useT(), primary = items[0]!;
  const brief = primary.title === "solGuide.review" && primary.tone === "info";
  return <section className={compact ? `${styles.guide} ${styles.compactGuide}` : styles.guide} aria-label={t("solGuide.title")} data-tone={primary.tone} data-brief={brief || undefined} data-refused={items.some(item => item.id === "operation" && item.tone === "warning")}>
    <div className={styles.nextStep}>
      <p role="status"><strong>{t(primary.title, primary.values)}</strong>
        {!brief && primary.body && (compact ? <small>{t(primary.body, primary.bodyValues)}</small> : <> — {t(primary.body, primary.bodyValues)}</>)}</p>
      {items.some(item => item.target === "conflicts") && <Button size="sm" disabled={localChanges}
        onClick={() => onNavigate("conflicts")}>{t("solGuide.action.conflicts")}</Button>}
      {primary.target !== "none" && (compact ? <button type="button" className={styles.compactDetails}
        aria-label={t("ws.details.title")} title={primary.body ? t(primary.body, primary.bodyValues) : t("ws.details.title")}
        onClick={onDetails}><Icon name="info" size={14} /></button>
        : <Button size="sm" variant="ghost" onClick={onDetails}>{t("ws.details.title")}</Button>)}
    </div>
    {conflictFields?.length > 0 && <p className={styles.intro}>{t("workflow.conflictFields", {
      fields: conflictFields.map(field => t(`field.${field}`, undefined, field)).join(", ") })}</p>}
    {items.slice(1).filter(item => item.tone === "danger" || item.id === "operation").map(item => <p key={item.id} className={item.tone === "danger" ? styles.independentError : styles.independentNotice}>
      {t(item.title, item.values)}{item.body && <> — {t(item.body, item.bodyValues)}</>}
    </p>)}
  </section>;
}

export function SolGuidance({ data, items, onNavigate, canOpenSources, onAssignment, localChanges, taskControls, expanded = false }: {
  data: RequestDetail; items: SolGuidanceItem[]; onNavigate: (target: GuidanceTarget) => void;
  canOpenSources: boolean; onAssignment?: () => void; localChanges: boolean;
  taskControls?: { disabled: boolean; reload: () => Promise<unknown> };
  expanded?: boolean;
}) {
  const t = useT(), primary = items[0]!;
  // Completion and unresolved delivery own the current step even when the retained workflow
  // has a stale basis. Its old tasks and refusal remain available in the history disclosure.
  const statusOnly = primary.id === "terminal" || primary.target === "delivery";
  const permission = !data.preparation.can_edit ? data.preparation.reason ?? "unavailable" : null;
  // In the reply pane the actual tasks own their descriptions, explanations and controls.
  // Keep their typed guidance only for the heading and the shared catalog destination.
  const inlineTask = Boolean(taskControls && primary.taskId);
  const source = taskControls ? items.find(item => item.taskId && item.target === "sources") : undefined;
  const more = items.slice(1).filter(item => (!taskControls || !item.taskId) && item.id !== permission);
  const action = (item: SolGuidanceItem, prominent = false) => item.target === "none" ? null : <>
    {item.target === "sources" && !canOpenSources
      ? <p className={styles.supporting}>{t("solGuide.adminSources")}</p>
      : <Button size="sm" variant={prominent ? "primary" : "secondary"} disabled={localChanges && !["tasks", "processing"].includes(item.target)}
        iconLeft={item.target === "sources" ? <Icon name="grid" size={16} /> : undefined}
        onClick={() => onNavigate(item.target)}>{t(`solGuide.action.${item.target}`)}</Button>}
  </>;
  const notices = <>
    {permission && <div className={styles.permission}>
      <Icon name="lock" size={20} />
      <div className={styles.permissionCopy}>
        {permission === "REQUEST_NOT_ASSIGNED" ? <>
          <strong>{t("solGuide.assignmentRequired")}</strong><p>{t("solGuide.assignmentAdmin")}</p>
        </> : <p>{t(`solGuide.permission.${permission}`, undefined, t("solGuide.permission.unavailable"))}</p>}
      </div>
      {permission === "REQUEST_NOT_ASSIGNED" && onAssignment && <Button size="sm" variant="ghost" disabled={localChanges}
        onClick={onAssignment}>{t("solGuide.assignment")}</Button>}
    </div>}
    {more.length > 0 && <details className={styles.more} open={more.some(item => item.tone === "danger" || item.tone === "warning")}>
      <summary className={styles.summary}>
        <Icon name="fileText" size={20} />
        <span className={styles.noticeTitle}>{more.map(item => t(item.title, item.values)).join(" · ")}</span>
        <span className={styles.chevron}><Icon name="chevronDown" size={16} /></span>
      </summary>
      <ul className={styles.notices}>{more.map(item => <li key={item.id}>
        <strong>{t(item.title, item.values)}</strong>{item.body && <p>{t(item.body, item.bodyValues)}</p>}{action(item)}
      </li>)}</ul>
    </details>}
  </>;
  const content = <div className={styles.content}>
    {localChanges && <p className={styles.supporting}>{t("solGuide.localChanges")}</p>}
    {statusOnly ? <>
      <p role="status">{t(primary.title, primary.values)}{primary.body && <> — {t(primary.body, primary.bodyValues)}</>}</p>
      {action(primary)}
    </> : <>
      {!data.workflow?.processing.in_progress && data.workflow?.reason && <p className={styles.intro}>{t(solRefusalGuidance(data.workflow).title)}</p>}
      {!data.workflow?.processing.in_progress && data.workflow?.conflict && <p className={styles.intro}>{t("workflow.conflictFields", { fields: data.workflow.conflict.fields.map(f => t(`field.${f}`, undefined, f)).join(", ") })}</p>}
      {!inlineTask && <>{primary.title === "solGuide.review" && primary.body && <p>{t(primary.body, primary.bodyValues)}</p>}{action(primary)}</>}
      {!!data.send_readiness?.questions.length && <Button size="sm" variant="ghost" disabled={localChanges} onClick={() => onNavigate("questions")}>{t("sendReady.questions.title")} · {data.send_readiness.questions.length}</Button>}
      {taskControls ? <WorkspaceTasks data={data} {...taskControls} guided showQuestions={!data.send_readiness?.questions.length} afterTasks={notices}
        taskAction={source ? { id: source.taskId!, content: action(source, inlineTask) } : undefined} /> : notices}
    </>}
  </div>;
  const heading = <>
    <Icon name={primary.tone === "danger" ? "xCircle" : "info"} size={20} />
    <span className={styles.title}>{t("ws.details.title")}</span>
  </>;
  return <section className={styles.guide} data-tone={primary.tone} aria-label={t("solGuide.title")}>
    {expanded ? content : <details className={styles.disclosure}>
      <summary className={styles.summary} aria-label={t("ws.details.title")}>
        {heading}<span className={styles.chevron}><Icon name="chevronDown" size={16} /></span>
      </summary>{content}
    </details>}
  </section>;
}

/** Retained content stays byte-for-byte the draft renderer's input; only its placement changes. */
export function SolDraftHistory({ historical, refused = false, editing, children }: { historical: boolean; refused?: boolean; editing: boolean; children: React.ReactNode }) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  if (!historical) return <>{children}</>;
  if (refused) return <section className={styles.history} aria-label={t("solGuide.outdatedDraft")}>
    <p><strong>{t("solGuide.outdatedDraft")}</strong></p>{children}
  </section>;
  return <details className={styles.history} open={editing || open} onToggle={e => { if (editing) e.currentTarget.open = true; else setOpen(e.currentTarget.open); }}>
    <summary>{t("solGuide.previousDraft")}</summary>{children}
  </details>;
}
